import type { AuctionFiltersInterface } from '@/modules/export/auction/list/components/filter/filtersSchema';
import { handleResult } from '@/helpers/utils/functionality-utils';
import { carrierModule } from '@/module/carrierModule';
import ApiService, { type RequestOptions } from '@/services/apiService';
import { isJson } from '@/helpers/utils/functionality-utils';
import type {
  PaginatedListAuctions,
  DataInterface,
  NullableAddUpdateAuctionHouseRequest,
  AddUpdateAuctionHouseResponse as AddUpdateAuctionHouseResponse,
} from '@/types/auction-types';

import type { StoreBaseResponse } from '@/types/types';

export class AuctionService {
  private auctionApiService: ApiService;

  constructor() {
    this.auctionApiService = new ApiService(carrierModule);
  }

  async getAuctionList(params: AuctionFiltersInterface['queryParameters']) {
    const response = await this.auctionApiService.get<
      PaginatedListAuctions & {
        info: { pending: number; completed: number };
      }
    >('/api/v1/auction/sheet/list', params);
    return handleResult(response);
  }

  // auction houses
  async getAuctionHouses() {
    const response = await this.auctionApiService.get<DataInterface[]>(
      '/api/v1/master/auction-houses',
    );
    return handleResult(response);
  }

  // upload auction sheets
  async uploadAuctionSheets(formData: FormData) {
    const auction_house_id = formData.get('auction_house');
    formData.delete('auction_house');
    const response = await this.auctionApiService.post<string>(
      `/api/v1/auction/sheet/${auction_house_id}/upstream`,
      formData,
    );
    return handleResult(response);
  }

  async deleteAuctionSheet(auction_sheet_id: number) {
    const response = await this.auctionApiService.delete<null>(
      `/api/v1/auction/sheet/${auction_sheet_id}/scrap`,
    );
    return handleResult(response);
  }

  async downloadAuctionSheet(auction_sheet_id: number) {
    const response = await this.auctionApiService.getFile<Blob>(
      `/api/v1/auction/sheet/${auction_sheet_id}/downstream`,
      undefined,
      { responseType: 'blob' },
    );
    const contentType = response.headers['content-type'];
    if (String(contentType)?.includes('application/json')) {
      const errorText = await response?.data?.text();
      const errorJson = JSON.parse(errorText);
      return handleResult({ ...response, data: errorJson });
    }
    if (response?.data instanceof Blob) {
      const blob = new Blob([response.data], {
        type: String(contentType) || 'application/octet-stream',
      });
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      const filename = filenameMatch ? filenameMatch[1] : 'abc';

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return handleResult({
        ...response,
        data: {
          status_code: 200,
          error: '',
          message: 'Success',
          data: null,
        },
      });
    }
    //@ts-expect-error this won't run, it's here to satisfy types
    return handleResult(response);
  }

  async downloadAuctionSheet_preview(auction_sheet_id: number) {
    const response = await this.auctionApiService.getFile<Blob>(
      `/api/v1/auction/sheet/${auction_sheet_id}/downstream`,
      undefined,
      { responseType: 'blob' },
    );
    const contentType = response.headers['content-type'];
    if (String(contentType)?.includes('application/json')) {
      const errorText = await response?.data?.text();
      const errorJson = JSON.parse(errorText);
      return handleResult({ ...response, data: errorJson });
    }
    if (response?.data instanceof Blob) {
      const blob = new Blob([response.data], {
        type: String(contentType) || 'application/octet-stream',
      });
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      const filename = filenameMatch ? filenameMatch[1] : 'abc';

      const url = window.URL.createObjectURL(blob);

      return handleResult({
        ...response,
        data: {
          status_code: 200,
          error: '',
          message: 'Success',
          data: { url, filename },
        },
      });
    }
    //@ts-expect-error this won't run, it's here to satisfy types
    return handleResult(response);
  }

  async getAuctionListForExport(
    params: RequestOptions['params'],
  ): Promise<StoreBaseResponse> {
    const response = await this.auctionApiService.getFile<Blob>(
      '/api/v1/auction/sheet/csv/export',
      params,
      { responseType: 'blob' },
    );
    const result: StoreBaseResponse = {
      error: false,
      message: '',
      status_code: 0,
    };

    if (await isJson(response.data)) {
      const errorText = await response.data.text();
      const errorJson = JSON.parse(errorText);
      result.error = true;
      result.message = errorJson.message;
      return result;

      // Handle the error accordingly
    } else if (response?.data instanceof Blob) {
      const filename =
        response.headers['content-disposition'].split('filename=')[1];

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      result.data = 'fetched Carriers';
      result.error = false;
      result.message = 'Success';
    }
    return result;
  }

  async addUpdateAuctionHouse(data: NullableAddUpdateAuctionHouseRequest) {
    const response =
      await this.auctionApiService.post<AddUpdateAuctionHouseResponse>(
        `/api/v1/master/auction/house/upsert`,
        data,
      );
    return handleResult(response);
  }
}
